import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { Navigate, createBrowserRouter, RouterProvider, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import queryString from 'query-string';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import { DidomiSDK } from '@didomi/react';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Rating } from '@mui/material';
import { clsx } from 'clsx';
// Components
import ToastContainer from './toastProvider';
import ResponsiveAppBar from '../../components/appBar/responsiveAppBar';
import lazyWrapper from '../../components/lazyWrapper';
import Redirect from './redirect';
import Space from '../../components/layout/space';
import AccordionBox from '../../components/accordionBox';
import TextCenterButton from '../../components/buttons/textCenterButton';
import LoaderFullPage from '../../components/loaderFullPage';
import BookingTrackingMobile from './bookingContainer/bookingTrackingMobile';
import SvgIcon from '../../components/icons/SvgIcon';
import Text from '../../components/text';
import { ModalSize } from '../../components/modals/createModalHOC';
import ModalButton, { ButtonType } from '../../components/modals/templates/modalButton';
// Resources
import { paths } from './paths';
import { getLogout, getMe } from '../../core/services/id';
import { getB2BGroupID, getB2CGroupID } from '../../utils/user';
import {
    KEY_RECENT_RESET,
    getDeepLinkActions,
    getUser,
    saveDeepLinkActions,
    asyncStorageApi,
    KEY_RECENT_SEARCH,
    KEY_LOCAL_STORAGE_NATIVE_OPEN,
    KEY_WV_STATUS_BAR,
    KEY_WV_STATUS_BAR_HEIGHT,
    KEY_LOCAL_STORAGE_NATIVE_OFFLINE,
    KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE,
    getTopBarHeight,
    saveLastLocation,
    KEY_LOCAL_STORAGE_NATIVE_FOREGROUND,
    KEY_LOCAL_STORAGE_NATIVE_REFETCH_LOCATION,
    KEY_SHOW_EXTERNAL,
    KEY_WV_APP_FROM,
    KEY_WV_MOBILE_VERSION,
    KEY_LOCAL_STORAGE_NATIVE_LAST_RIDE_FETCHED,
    KEY_LOCAL_STORAGE_NATIVE_DOWNLOAD_BANNER,
    KEY_LOCAL_STORAGE_NATIVE_FOREGROUND_TIME,
    getLastLocation,
    KEY_LOCAL_STORAGE_ONBOARDING_DONE,
    KEY_WV_NAV_BAR_HEIGHT,
    KEY_SHOW_WICKED,
    KEY_LISTING_PAGINATION,
} from '../../localStorageAPI';
import { AppFrom, BaseUser, DeepLink, EventType, GetEventOutput, Language } from '../../core/apiTypes';
import envConfig from '../../config/currentEnv';
import { iconToCdn } from '../../utils/images';
import { IconAxel, IconCommon, IconError, IconLogo } from '../../resources/images';
import ModalContainer from '../modalContainer';
// Modules
import {
    selectors as currentBookingSelectors,
    actions as currentBookingActions,
} from '../../core/modules/currentBooking';
import { selectors as userSelectors, actions as userActions } from '../../core/modules/user';
import { ActionOpenModal, ModalType, actions as modalActions } from '../../core/modules/modals';
// Resources
import { stringsRes } from '../../resources/strings';
import { redirectToAuth, redirectToStore } from '../../utils/redirect';
import appConfig from '../../config/appConfig';
import featureFlag from '../../config/featureFlag';
import packageJson from '../../../package.json';
import {
    WebViewChannel,
    isDeviceAndroid,
    isWebView,
    isWebViewMobile,
    sendMessageToNativeApp,
} from '../../utils/webview';
import { NavTopBarHeight, REFRESH_LOCATION_THRESHOLD } from '../../utils/constants';
import { UseLocationOutput } from '../../components/hook/useLocation';
import LCEvent from '../../utils/customEvents';
import { getToken, hasToken, isValidJWT } from '../../core/services/helpers/axios';
import useGlobalStyles from '../../components/hook/useGlobalStyles';
import configs from '../../config';
import { isTrue } from '../../utils/strings';
import { distanceBetween } from '../../utils/map';
import { sendDidomiEvent } from '../../config/didomi';
import { isEmpty } from '../../utils/basics';
import { useBreakpoints, WebLocation } from '../../customHooks/useBreakpoints';
import { useTokenRefresh } from '../../customHooks/useTokenRefresh';
import { AxiosError } from 'axios';
import { useEvent } from '../../hooks/useEvent';
import { useGetEvent } from '../../core/services/events';

//= ====================================//
//= ============== Pages ===============//
//= ====================================//

const HomePage = React.lazy(() => lazyWrapper(() => import('./home')));
// const AdminPage = React.lazy(() => lazyWrapper(() => import('./admin/admin')) );
const AdminGroupsPage = React.lazy(() => lazyWrapper(() => import('./admin/adminGroups')));
const AdminAddGroupsPage = React.lazy(() => lazyWrapper(() => import('./admin/adminGroups/addGroups')));
const AdminAddTravelPolicyPage = React.lazy(() => lazyWrapper(() => import('./admin/adminGroups/addTravelPolicy')));
const AdminEditGroupsPage = React.lazy(() => lazyWrapper(() => import('./admin/adminGroups/editGroup')));
const AdminEditGroupsManageCollabPage = React.lazy(() =>
    lazyWrapper(() => import('./admin/adminGroups/editGroupManageCollab')),
);
const AdminImportBulk = React.lazy(() => lazyWrapper(() => import('./admin/adminShared/importBulk')));
const AdminImportBulkPrepare = React.lazy(() => lazyWrapper(() => import('./admin/adminUsers/importBulkPrepare')));
const AdminUsersPage = React.lazy(() => lazyWrapper(() => import('./admin/adminUsers')));
const AdminUsersAddPage = React.lazy(() => lazyWrapper(() => import('./admin/adminShared/addUsers')));
const AdminAssociateUsersPage = React.lazy(() => lazyWrapper(() => import('./admin/adminGroups/associateUsers')));
const AdminEditUsersPage = React.lazy(() => lazyWrapper(() => import('./admin/adminShared/editUser')));
const AdminPayment = React.lazy(() => lazyWrapper(() => import('./admin/admin/payment')));
const AdminPaymentEdit = React.lazy(() => lazyWrapper(() => import('./payment/paymentEdit')));
const AdminBillingPage = React.lazy(() => lazyWrapper(() => import('./admin/admin/billing')));

const DebugPage = React.lazy(() => lazyWrapper(() => import('./debug')));
const HBPage = React.lazy(() => lazyWrapper(() => import('./hb')));

const ProfilePage = React.lazy(() => lazyWrapper(() => import('./profile')));
const ProfileEditPhonePage = React.lazy(() => lazyWrapper(() => import('./profile/editProfilePhone')));
const ProfileCheckMailboxPage = React.lazy(() => lazyWrapper(() => import('./profile/profileCheckMailbox')));
const ProfileValidatePhonePage = React.lazy(() => lazyWrapper(() => import('./profile/profileValidatePhone')));
const ProfileValidateCodePage = React.lazy(() => lazyWrapper(() => import('./profile/profileValidateCode')));
const EditProfileInfosPage = React.lazy(() => lazyWrapper(() => import('./profile/editProfileInfos')));
const LegalInfosPage = React.lazy(() => lazyWrapper(() => import('./profile/legalInfos')));
// const EditProfilePwdPage = React.lazy(() => lazyWrapper(() => import('./profile/editProfilePwd')) );

const ReservationsPage = React.lazy(() => lazyWrapper(() => import('./reservations')));
const ReservationDetailsPage = React.lazy(() => lazyWrapper(() => import('./reservations/reservationDetails')));
const ReservationEditRideReason = React.lazy(() =>
    lazyWrapper(() => import('./reservations/editReservationRideReason')),
);
const ReservationEditDriverMessage = React.lazy(() =>
    lazyWrapper(() => import('./reservations/editReservationDriverMessage')),
);
const ReservationEditPayment = React.lazy(() => lazyWrapper(() => import('./reservations/editReservationPayment')));
const ReservationEditPassengers = React.lazy(() =>
    lazyWrapper(() => import('./reservations/editReservationPassengers')),
);
const ReservationEditPoiNumber = React.lazy(() => lazyWrapper(() => import('./reservations/editReservationPoiNumber')));
const ReservationEditMeetingPoint = React.lazy(() =>
    lazyWrapper(() => import('./reservations/editReservationMeetingPoint')),
);
const SOSPage = React.lazy(() => lazyWrapper(() => import('./sos')));
const TripHistory = React.lazy(() => lazyWrapper(() => import('./tripHistory')));
const TripHistoryDetails = React.lazy(() => lazyWrapper(() => import('./tripHistory/tripHistoryDetails')));
const FavoritesPage = React.lazy(() => lazyWrapper(() => import('./favorites')));
const FavoriteCreateOrEditPage = React.lazy(() => lazyWrapper(() => import('./favorites/addOrEditFavorite')));
const FamilyPage = React.lazy(() => lazyWrapper(() => import('./family')));
const FamilyAddOrEditPage = React.lazy(() => lazyWrapper(() => import('./family/addOrEditFamilyMember')));
const SupportPage = React.lazy(() => lazyWrapper(() => import('./support')));
const PaymentPage = React.lazy(() => lazyWrapper(() => import('./payment')));
const PaymentAdyenPage = React.lazy(() => lazyWrapper(() => import('./payment/dropinAdyen')));
const PaymentRedirectPage = React.lazy(() => lazyWrapper(() => import('./payment/paymentRedirect')));
const PaymentEditPage = React.lazy(() => lazyWrapper(() => import('./payment/paymentEdit')));
const PromoPage = React.lazy(() => lazyWrapper(() => import('./promo')));
const RideTrackingMobilePage = React.lazy(() => lazyWrapper(() => import('./bookingSoonContainer')));
const SelfServiceCompany = React.lazy(() => lazyWrapper(() => import('./selfService/selfServiceCompany')));
const SelfServiceFormula = React.lazy(() => lazyWrapper(() => import('./selfService/selfServiceFormula')));
const SelfServiceSignContract = React.lazy(() => lazyWrapper(() => import('./selfService/selfServiceSignContract')));
const SelfServiceWelcome = React.lazy(() => lazyWrapper(() => import('./selfService/selfServiceWelcome')));
const SelfServicePayment = React.lazy(() => lazyWrapper(() => import('./selfService/selfServicePayment')));
const SelfServicePaymentSettings = React.lazy(() =>
    lazyWrapper(() => import('./selfService/selfServicePaymentSettings')),
);
const SelfServicePolicies = React.lazy(() => lazyWrapper(() => import('./selfService/selfServicePolicies')));

//= ====================================//
//= ============== Error ===============//
//= ====================================//

interface ErrorProps {
    error: Error;
}

const ErrorHandler = (props: ErrorProps) => {
    console.log('Log from ErrorHandler');
    const classes = useErrorStyles();
    const { error } = props;

    React.useEffect(() => {
        if (appConfig.useSentry) {
            const axiosError = (error as AxiosError).isAxiosError ? (error as AxiosError) : null;
            Sentry.captureException(
                error,
                axiosError
                    ? {
                          extra: {
                              url: axiosError.config?.url,
                              method: axiosError.config?.method,
                              requestData: axiosError.config?.data,
                              responseStatus: axiosError.response?.status,
                              responseData: axiosError.response?.data,
                          },
                      }
                    : {},
            );
        }
        // Webview only : redirect to Auth on error
        if (isWebView()) {
            redirectToAuth('Code - Webview crash');
        }
    }, [error]);

    // this is for lock button firefox that removes page datas without triggering storage event.
    // This will render error in navigator but you won't see the error page and it redirects you directly to auth.
    const cookieExists = hasToken();
    if (!cookieExists) {
        redirectToAuth('Code - fzCNTjTU');
        return null;
    }

    return (
        <>
            <ResponsiveAppBar shouldRefresh />
            <div role="alert" className={classes.alertContainer}>
                <div className={classes.alertPadding} />
                <div className={classes.errorContainer}>
                    <img alt="error" src={iconToCdn(IconError.Error).replace('.svg', '.png')} width="50%" />
                </div>
                <Space value={4} />
                {/* <Text.Description16
                    text={null}
                    dangerouslySetInnerHTML={{ __html: stringsRes.errors.clientError }}
                    className={classes.errorText}
                />
                <Space value={4} /> */}
                <div className={classes.refreshButtonContainer}>
                    <TextCenterButton
                        onClick={() => {
                            if (window.location.pathname === paths.home) {
                                window.location.reload();
                            } else {
                                window.location.href = `${window.location.origin}${paths.home}`;
                            }
                        }}
                        title={stringsRes.common.back}
                    />
                </div>
                <Space value={2} />
                <AccordionBox title={error.message} className={classes.accordionContainer}>
                    <div className={classes.accordionWrapper}>
                        <pre>{error.stack}</pre>
                    </div>
                </AccordionBox>
            </div>
        </>
    );
};

const useErrorStyles = makeStyles(() =>
    createStyles({
        alertContainer: {
            marginTop: 50,
        },
        alertPadding: {
            paddingTop: 50,
        },
        errorContainer: {
            textAlign: 'center',
        },
        errorText: {
            display: 'flex',
            justifyContent: 'center',
            marginRight: 16,
            marginLeft: 16,
        },
        accordionContainer: {
            width: '85%',
            margin: 'auto !important',
        },
        accordionWrapper: {
            width: 'fit-content',
            margin: 'auto',
        },
        refreshButtonContainer: {
            display: 'flex',
            margin: 'auto',
            width: 'fit-content',
        },
    }),
);

// Screens that close the booking flow: the ride exists, so the screens it was booked from are done
// with (and their booking data already cleared). Going back from one of them means going home.
const flowEndPaths = [paths.homeTrackingMobile.replace(':id', ''), paths.homeConfirm];
const isFlowEndPath = (pathname: string): boolean => flowEndPaths.some((el) => pathname.startsWith(el));

const Layout = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const previousPathnameRef = useRef<string | null>(null);

    useEffect(() => {
        console.log('Currently here :', pathname);
        document.documentElement.setAttribute('data-page-url', pathname);
        if (pathname === paths.home && previousPathnameRef.current !== null) {
            sendMessageToNativeApp(WebViewChannel.ASK_LOCATION, {});
        }

        // Reset KEY_LISTING_PAGINATION when leaving listing pages
        // if the keys of currentSaved are not included in the current pathname then delete them
        const currentSaved = asyncStorageApi.restoreData(KEY_LISTING_PAGINATION) || {};
        const newSaved = Object.keys(currentSaved).reduce((acc, key) => {
            if (pathname.startsWith(key)) {
                acc[key] = currentSaved[key];
            }
            return acc;
        }, {});
        asyncStorageApi.persistData(KEY_LISTING_PAGINATION, newSaved);

        previousPathnameRef.current = pathname;
    }, [pathname]);

    useEffect(() => {
        window.nativeGoBack = () => {
            console.log('Application nativeApp ask to go to next step');
            if (pathname === paths.home) {
                return false;
            } else if (isFlowEndPath(pathname)) {
                // Same reason as the tracking's own back arrow: the booking is over, and popping the
                // stack would land on a booking screen whose own guards bounce to an empty home.
                navigate(paths.home);
                return true;
            } else {
                navigate(-1);
                return true;
            }
        };
    }, [navigate, pathname]);

    return (
        <>
            <ErrorBoundary FallbackComponent={ErrorHandler}>
                <ModalContainer />
                <ResponsiveAppBar />
                <Outlet />
            </ErrorBoundary>
        </>
    );
};

//= ===================================//
//= ============== Root ===============//
//= ===================================//

const isDev: boolean = configs?.appConfig?.isDev;

interface RootContainerProps {
    lang: string;
    groupId: string;
    changeLanguage: (lang: Language) => any;
    setGroupId: (groupId: string) => any;
    setPassengers: (passengers: BaseUser[]) => any;
    setReservationDate: (date: any) => any;
    setSelectedService: (service: string) => any;
    openModal: ActionOpenModal;
}

const RootContainer: FC<RootContainerProps> = ({
    lang,
    groupId,
    setGroupId,
    setPassengers,
    setReservationDate,
    setSelectedService,
    changeLanguage,
    openModal,
}): ReactElement => {
    console.log('Log from RootContainer');

    const classes = useStyles();
    const globalClasses = useGlobalStyles();
    const { isMobileSize } = useBreakpoints(WebLocation.FLOW);

    // URL Params
    const parsed = queryString.parse(window.location.search);

    const appFrom = asyncStorageApi.restoreDataNoLink(KEY_WV_APP_FROM);

    const isOffline = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE);
    const isOfflineBannerVisible = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE);

    const downloadBanner: boolean = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_DOWNLOAD_BANNER);
    const [showDlBanner, setShowDlBanner] = useState<boolean>(downloadBanner ?? true);
    const [isConnected, setConnected] = useState<boolean>(!isEmpty(getUser()) && isValidJWT());
    const [offlineInfo, setOfflineInfo] = useState<{ isOffline: boolean; isOfflineBannerVisible: boolean }>({
        isOffline,
        isOfflineBannerVisible,
    });
    const didomiObjectRef = useRef<any>(null);

    // Proactive JWT renewal while we hold a session. Owns its own timer and rearms it on every
    // foreground, so a renew missed while the webview was suspended is caught up on resume.
    useTokenRefresh({ enabled: isConnected, onError: () => setConnected(false) });

    //= =====================================//
    //= ============== EVENTS ===============//
    //= =====================================//
    const { currentEvent, setEvent } = useEvent();
    const isWicked = currentEvent === EventType.WICKED;

    useGetEvent<GetEventOutput>({
        enabled: featureFlag.eventCheck.active && isConnected,
        variables: { event: EventType.WICKED.toLocaleLowerCase() as any },
        onSuccess: (data: GetEventOutput) => {
            setEvent(data.hasAccess ? EventType.WICKED : null);
        },
        retry: 0,
    });

    //= =========================================================//
    //= ============== NATIVE AVAIALBLE FUNCTIONS ===============//
    //= =========================================================//
    // Search for native functions in the app - window.native
    // Registered once instead of on every render: assigning them in the render body made this
    // (frequently re-rendering) component rebuild 8 closures per pass. `[]` is safe because none
    // of the handlers read component state - the only binding they capture is setOfflineInfo,
    // whose identity React keeps stable. They are deliberately NOT removed on unmount: the
    // native app can call into the bridge at any time, and a missing function is worse than one
    // holding a closure from an earlier render.
    useEffect(() => {
        // Code called by Native app to tell us if the app was just launched (not restarted from background)
        window.nativeAppStarted = () => {
            console.log('Application nativeApp Started');
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OPEN, true);
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_LAST_RIDE_FETCHED, false);
        };

        window.nativeAppOffline = () => {
            console.log('Application nativeApp is Offline');
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE, true);
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE, true);
            setOfflineInfo({ isOffline: true, isOfflineBannerVisible: true });

            // Dispatch event Background for the rest of the app
            window.dispatchEvent(new CustomEvent(LCEvent.OFFLINE, { detail: { offline: true } }));
        };

        window.nativeAppOnline = () => {
            console.log('Application nativeApp is Online');
            setTimeout(() => {
                asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE, false);
                asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE, true);
                setOfflineInfo({ isOffline: false, isOfflineBannerVisible: true });

                // Dispatch event Background for the rest of the app
                window.dispatchEvent(new CustomEvent(LCEvent.ONLINE, { detail: { online: true } }));
            }, 2000);
        };

        window.nativeAppGetToken = () => {
            console.log('Application nativeApp asked for Token');
            return getToken();
        };

        window.nativeAppForeground = () => {
            console.log('Application nativeApp changed to Foreground');
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_FOREGROUND, true);
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_FOREGROUND_TIME, new Date().getTime());
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_REFETCH_LOCATION, true);

            // Dispatch event Foreground for the rest of the app
            window.dispatchEvent(new CustomEvent(LCEvent.FOREGROUND, { detail: { foreground: true } }));
        };

        window.nativeAppBackground = () => {
            console.log('Application nativeApp changed to Background');
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_FOREGROUND, false);
            asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_FOREGROUND_TIME, new Date().getTime());

            // Dispatch event Background for the rest of the app
            window.dispatchEvent(new CustomEvent(LCEvent.BACKGROUND, { detail: { background: true } }));
        };

        window.nativeAppNotification = () => {
            console.log('Application nativeApp received notification');

            // Dispatch event notification for the rest of the app
            window.dispatchEvent(new CustomEvent(LCEvent.NOTIFICATION, { detail: { notification: true } }));
        };

        window.nativeAppUpdateLocation = (datas: string | any) => {
            // console.log('Application nativeApp updated Location received', datas);
            // example :
            // window.nativeAppUpdateLocation({
            //     timestamp: new Date().getTime(),
            //     latitude: 48.8826228,
            //     longitude: 2.241228,
            //     altitude: null,
            //     horizontalAccuracy: null,
            //     verticalAccuracy: null,
            // })

            let position;

            if (typeof datas === 'object' && datas !== null) {
                position = datas;
            } else if (typeof datas === 'string') {
                try {
                    position = JSON.parse(datas);
                } catch (e) {
                    console.error('Failed to parse JSON string:', e);
                    position = null; // Or handle the error as needed
                }
            } else {
                console.error('Invalid type for datas:', typeof datas);
                position = null; // Or handle the error as needed
            }

            if (!position) {
                console.error('Exiting nativeAppUpdateLocation without saving last location');
                return;
            }

            const res: UseLocationOutput = {
                position: { ...position },
                latLng: {
                    lat: +position?.latitude?.toFixed(7) || null,
                    lng: +position?.longitude?.toFixed(7) || null,
                },
                error: null,
            };

            const lastLocation = getLastLocation();
            const distance = distanceBetween(lastLocation?.latLng, res.latLng);

            // check if lastLocation is further than 500 meters than position
            if (distance > REFRESH_LOCATION_THRESHOLD) {
                asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_REFETCH_LOCATION, true);
                window.dispatchEvent(new CustomEvent(LCEvent.FOREGROUND, { detail: { foreground: true } }));
            } else {
                window.dispatchEvent(new CustomEvent(LCEvent.NEW_POSITION, { detail: { location: res } }));
            }
            // console.log('=== position ', position, ' res ', res);
            saveLastLocation(res);
        };
    }, []);

    //= ===============================================//
    //= ============== PARSED & Actions ===============//
    //= ===============================================//
    // Reset recent search if flag is true and we are on a different version than last time we reset
    if (
        featureFlag.resetRecent.active &&
        asyncStorageApi.restoreData(KEY_RECENT_RESET)?.version !== packageJson.version
    ) {
        asyncStorageApi.clearData(KEY_RECENT_SEARCH);
        asyncStorageApi.persistData(KEY_RECENT_RESET, { version: packageJson.version });
    }

    if (parsed.action) {
        saveDeepLinkActions(parsed as unknown as DeepLink);

        // Remove all query parameters from the URL without reloading
        const newUrl = window.location.origin + window.location.pathname;
        window.history.replaceState(null, '', newUrl);
    }

    if (parsed.statusBarHeight) {
        asyncStorageApi.persistDataNoLink(KEY_WV_STATUS_BAR_HEIGHT, parsed.statusBarHeight);
    }

    // Compared to undefined, not truthiness: "0" is a legitimate value (no bottom bar) and skipping
    // it would leave the height measured on a previous launch — or a previous navigation mode — in
    // place for good.
    if (parsed.navBarHeight !== undefined) {
        asyncStorageApi.persistDataNoLink(KEY_WV_NAV_BAR_HEIGHT, parsed.navBarHeight);
    }

    if (parsed.mobileVersion) {
        asyncStorageApi.persistDataNoLink(KEY_WV_MOBILE_VERSION, parsed.mobileVersion);
    }

    if (parsed.app) {
        asyncStorageApi.persistDataNoLink(KEY_WV_APP_FROM, parsed.app);
    } else {
        asyncStorageApi.clearDataNoLink(KEY_WV_APP_FROM);
    }

    //= =========================================//
    //= ============== USE EFFECT ===============//
    //= =========================================//
    useEffect(() => {
        if (isWicked) {
            const num = asyncStorageApi.restoreDataNoLink(KEY_SHOW_WICKED) ?? 0;
            if (num <= 0) {
                openModal(ModalType.WICKED, {
                    title: stringsRes.wicked.title,
                    modalSize: ModalSize.sm,
                    ...(isMobileSize && { floatingClose: 'center', modalPosition: 'bottom' }),
                    modalStyle: clsx(isMobileSize && globalClasses.modalBottomRating), //isWebView() && classes.statusBarTop,
                });
            }
            asyncStorageApi.persistDataNoLink(KEY_SHOW_WICKED, num + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isWicked]);

    useEffect(() => {
        if (isConnected && appFrom === AppFrom.MARCEL) {
            const num = asyncStorageApi.restoreDataNoLink(KEY_SHOW_EXTERNAL) ?? 0;
            if (num <= 0 || num % 2 === 0) {
                openModal(ModalType.MARCEL, {
                    modalSize: ModalSize.md,
                    onConfirmAction: () => {
                        redirectToStore();
                    },
                });
            }
            asyncStorageApi.persistDataNoLink(KEY_SHOW_EXTERNAL, num + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appFrom, isConnected]);

    useEffect(() => {
        if (isConnected) {
            if (featureFlag.onBoarding.active) {
                const num = asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_ONBOARDING_DONE) ?? 0;
                if (num <= 0) {
                    openModal(ModalType.ONBOARDING, {
                        floatingClose: 'none',
                        ...(isMobileSize && { modalPosition: 'bottom' }),
                        modalSize: isMobileSize ? ModalSize.lg : ModalSize.sm,
                        modalStyle: clsx(globalClasses.modalRating, isMobileSize && globalClasses.modalBottomRating),
                    });
                }
            } else {
                asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_ONBOARDING_DONE, 1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected, featureFlag.onBoarding.active]);

    useEffect(() => {
        if (!offlineInfo?.isOffline && !groupId) {
            getMe(setConnected, setGroupId, setPassengers, changeLanguage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setGroupId, setPassengers, changeLanguage, offlineInfo.isOffline]);

    // Handle lang change
    useEffect(() => {}, [lang]);

    // LOG debug
    useEffect(() => {
        sendMessageToNativeApp(WebViewChannel.LOG, 'First time in rootContainer');
    }, []);

    // Handle remove storage to avoid crashes
    useEffect(() => {
        const handleStorageChange = (/* e: StorageEvent */) => {
            // Reactive the offline banner if needed
            setOfflineInfo({
                isOffline: asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE),
                isOfflineBannerVisible: asyncStorageApi.restoreDataNoLink(
                    KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE,
                ),
            });

            // Check if the cookies you are interested in still exist
            const cookieExists = hasToken();

            if (!cookieExists || !getUser()) {
                // Sentry.captureMessage(
                //     `Storage Changed no cookie ${!cookieExists} user ${!getUser()}, ${e.key}, ${e.oldValue}, ${
                //         e.newValue
                //     }, ${e.url}, ${e.storageArea}`,
                // );
                // We don't reactivate this cause sometimes it passes by here
                // redirectToAuth(`Storage Changed cookie ${cookieExists} user ${!getUser()}`);
            }
        };

        const handleForeground = () => {
            if (!isValidJWT()) {
                setConnected(false);
                if (!offlineInfo?.isOffline && !groupId) {
                    getMe(setConnected, setGroupId, setPassengers, changeLanguage);
                }
            }
        };

        // Attach the event listener when the component mounts
        // TOKEN_RENEWED runs handleForeground on purpose: a renewal that worked is a no-op
        // there (the JWT is valid), and one that did not land re-bootstraps the session.
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(LCEvent.FOREGROUND, handleForeground);
        window.addEventListener(LCEvent.TOKEN_RENEWED, handleForeground);

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(LCEvent.FOREGROUND, handleForeground);
            window.removeEventListener(LCEvent.TOKEN_RENEWED, handleForeground);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        asyncStorageApi.persistDataNoLink(KEY_WV_STATUS_BAR, true);

        // Handle showing download Banner at every reload - we could remove the storage but maybe will be useful later
        asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_DOWNLOAD_BANNER, true);
        setShowDlBanner(!isDev); // Disabled in DEV
    }, []);

    useEffect(() => {
        if (isConnected) {
            // sending event to native app if webview
            sendMessageToNativeApp(WebViewChannel.FINISH_LOADING, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected]);

    if (!isConnected) {
        return (
            <LoaderFullPage
                msg={
                    offlineInfo?.isOffline
                        ? stringsRes.errors.offline
                        : `${stringsRes.common.loading} ${stringsRes.profile.profile}`
                }
                backgroundColor="lightblue"
                withIcon={!appFrom || appFrom !== AppFrom.MARCEL}
            />
        );
    }

    const deepLinkAction = getDeepLinkActions()?.[0];
    if (deepLinkAction?.action === 'book') {
        const { date, service, account } = deepLinkAction;
        const user = getUser();
        if (
            deepLinkAction.groupKey &&
            (!isTrue(deepLinkAction.exist) ||
                !user.emails.some(({ email }) => email === decodeURIComponent(deepLinkAction.smail)))
        ) {
            getLogout();
            return <></>;
        }

        if (date) {
            setReservationDate(moment(date * 1000));
        }
        setSelectedService(service ?? 'sncfpap');
        setGroupId(account === 'perso' ? getB2CGroupID() : getB2BGroupID());
    }

    // Imbriquer pour sous routes potentiellement
    //     <Route path="users" element={<Users />}>
    //     <Route path="me" element={<OwnUserProfile />} />
    //     <Route path=":id" element={<UserProfile />} />
    //   </Route>

    const router = createBrowserRouter(
        [
            {
                element: <Layout />,
                children: [
                    { path: paths.hb, element: <HBPage /> },
                    { path: `${paths.home}/*`, element: <HomePage /> },
                    // Ride Tracking
                    { path: paths.homeTrackingMobile, element: <BookingTrackingMobile /> },
                    // Profile
                    { path: paths.profile, element: <ProfilePage /> },
                    { path: paths.profileEditPhone, element: <ProfileEditPhonePage /> },
                    { path: paths.profileCheckMail, element: <ProfileCheckMailboxPage /> },
                    { path: paths.profileValidatePhone, element: <ProfileValidatePhonePage /> },
                    { path: paths.profileValidateCode, element: <ProfileValidateCodePage /> },
                    { path: paths.profileEditInfos, element: <EditProfileInfosPage /> },
                    { path: paths.profileLegalInfos, element: <LegalInfosPage /> },
                    // {path: paths.profileEditPwd, element: <EditProfilePwdPage />},

                    // Reservations
                    { path: paths.reservations, element: <ReservationsPage /> },
                    { path: paths.reservationsDetail, element: <ReservationDetailsPage /> },
                    { path: paths.reservationsRecurrentDetail, element: <ReservationDetailsPage recurrent /> },
                    { path: paths.reservationsEditRideReason, element: <ReservationEditRideReason /> },
                    { path: paths.reservationsEditDriverMessage, element: <ReservationEditDriverMessage /> },
                    { path: paths.reservationsEditPayment, element: <ReservationEditPayment /> },
                    { path: paths.reservationsEditPassengers, element: <ReservationEditPassengers /> },
                    { path: paths.reservationsEditPoiNumber, element: <ReservationEditPoiNumber /> },
                    { path: paths.reservationsEditMeetingPoint, element: <ReservationEditMeetingPoint /> },
                    // TripHistory
                    { path: paths.tripHistory, element: <TripHistory /> },
                    { path: paths.tripHistoryDetail, element: <TripHistoryDetails /> },
                    // Favoris
                    { path: paths.favorites, element: <FavoritesPage /> },
                    { path: paths.favoriteCreateOrEdit, element: <FavoriteCreateOrEditPage /> },
                    // Famille
                    { path: paths.family, element: <FamilyPage /> },
                    { path: paths.addOrEditFamily, element: <FamilyAddOrEditPage /> },
                    // Payment
                    { path: paths.payment, element: <PaymentPage /> },
                    { path: paths.paymentAdyenNew, element: <PaymentAdyenPage /> },
                    { path: paths.paymentRedirect, element: <PaymentRedirectPage /> },
                    { path: paths.paymentEditCard, element: <PaymentEditPage /> },
                    // Promo
                    { path: paths.promo, element: <PromoPage /> },
                    // Admin
                    // {path: paths.admin, element: <AdminPage />},
                    { path: paths.adminUsers, element: <AdminUsersPage /> },
                    { path: paths.adminUsersAdd, element: <AdminUsersAddPage /> },
                    { path: paths.adminUsersEdit, element: <AdminEditUsersPage /> },
                    { path: paths.adminGroups, element: <AdminGroupsPage /> },
                    { path: paths.adminGroupsAdd, element: <AdminAddGroupsPage /> },
                    { path: paths.adminGroupsTravelPolicy, element: <AdminAddTravelPolicyPage /> },
                    { path: paths.adminGroupsEdit, element: <AdminEditGroupsPage /> },
                    { path: paths.adminImportBulk, element: <AdminImportBulk /> },
                    { path: paths.adminImportBulkPrepare, element: <AdminImportBulkPrepare /> },
                    { path: paths.adminGroupsManageCollab, element: <AdminEditGroupsManageCollabPage /> },
                    { path: paths.adminUsersAssociate, element: <AdminAssociateUsersPage /> },
                    { path: paths.adminPayment, element: <AdminPayment /> },
                    { path: paths.adminEditPayment, element: <AdminPaymentEdit /> },
                    { path: paths.adminPaymentAdyenNew, element: <PaymentAdyenPage /> },
                    { path: paths.adminBilling, element: <AdminBillingPage /> },
                    // Divers
                    ...(process.env.REACT_APP_URL_FAQ ? [] : [{ path: paths.support, element: <SupportPage /> }]),
                    { path: paths.rideTrackingMobile, element: <RideTrackingMobilePage /> },
                    // SelfService
                    ...(featureFlag.selfService.active
                        ? [
                              { path: paths.selfServiceCompany, element: <SelfServiceCompany /> },
                              { path: paths.selfServiceFormula, element: <SelfServiceFormula /> },
                              { path: paths.selfServiceSignContract, element: <SelfServiceSignContract /> },
                              { path: paths.selfServiceWelcome, element: <SelfServiceWelcome /> },
                              { path: paths.selfServicePayment, element: <SelfServicePayment /> },
                              { path: paths.selfServicePaymentSettings, element: <SelfServicePaymentSettings /> },
                              { path: paths.selfServicePolicies, element: <SelfServicePolicies /> },
                          ]
                        : []),
                    // Concur
                    {
                        path: paths.concur,
                        element: <Redirect url={`${envConfig.currentEnv.authURL}${paths.concur}`} />,
                    },
                    // DEBUG TO REMOVE
                    { path: paths.debug, element: <DebugPage /> },
                    // SOS
                    { path: paths.sos, element: <SOSPage /> },

                    // All the rest
                    { path: '*', element: <Navigate to={paths.home} replace /> },
                ],
            },
        ],
        // React Router Future Flag Warning:
        // {
        //     future: {
        //         v7_relativeSplatPath: true,
        //         v7_startTransition: true,
        //     },
        // },
    );

    //= ======================================//
    //= ============== Actions ===============//
    //= ======================================//

    const onCloseOfflineBanner = (): void => {
        asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE, false);
        setOfflineInfo({
            isOffline: offlineInfo.isOffline,
            isOfflineBannerVisible: false,
        });
    };

    const onCloseDownloadBanner = (): void => {
        asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_NATIVE_DOWNLOAD_BANNER, false);
        setShowDlBanner(false);
    };

    const onClickDownload = (): void => {
        redirectToStore();
    };

    //= =====================================//
    //= ============== Didomi ===============//
    //= =====================================//

    /**
     * Called once we have the callback from the SDK informing that Didoni is loaded and ready
     */
    const onDidomiReady = (didomi): void => {
        didomiObjectRef.current = didomi; // Store in ref instead of state
        sendDidomiEvent(didomi);
    };

    /**
     * Called everytime the consent changes
     */
    const consentHasChanged = (cwtToken: string): void => {
        console.log('Didomi Consent Changed - cwtToken : ', cwtToken);
        if (didomiObjectRef.current) {
            sendDidomiEvent(didomiObjectRef.current);
        }
    };

    //= ======================================//
    //= ============== Render ================//
    //= ======================================//
    return (
        <React.Suspense
            fallback={
                <LoaderFullPage
                    msg={`_${stringsRes.common.loading}_ ${
                        !configs.envConfig.currentEnv.isProduction ? `${window.location.pathname} ${groupId}` : ''
                    }`}
                />
            }
        >
            {featureFlag.didomi.active && (
                <DidomiSDK
                    apiKey={envConfig.currentEnv.didomi.apiKey}
                    noticeId={envConfig.currentEnv.didomi.noticeKey}
                    onReady={onDidomiReady}
                    onConsentChanged={consentHasChanged}
                />
            )}
            <Paper className={classes.rootContainerRoot}>
                <ToastContainer />
                {offlineInfo?.isOffline && offlineInfo?.isOfflineBannerVisible && (
                    <div className={classes.rootContainerBandeauOffline}>
                        <SvgIcon icon={IconCommon.Offline} color="allWhite" />
                        <Text.ShortMediumText14 text={stringsRes.errors.offline} customColor="bothWhite" />
                        <Button
                            startIcon={<SvgIcon icon={IconCommon.CircleClose} color="allWhite" fill="transparent" />}
                            onClick={onCloseOfflineBanner}
                        />
                    </div>
                )}
                {isWebViewMobile() && showDlBanner && (
                    <>
                        <div className={classes.rootContainerBandeauDownload}>
                            <Button
                                startIcon={<SvgIcon icon={IconCommon.Close} fill="transparent" color="black" />}
                                onClick={onCloseDownloadBanner}
                            />
                            <div className={classes.centerBandeauDownload}>
                                <SvgIcon icon={IconLogo.AppIcon} size="xxxlarge" stroke="unset" />
                                <div className={globalClasses.flexCol}>
                                    <Text.ShortMediumText14
                                        text={stringsRes.download[isDeviceAndroid() ? 'android' : 'ios']}
                                    />
                                    <Rating name="rating" value={4.5} precision={0.5} readOnly size="small" />
                                </div>
                            </div>
                            <div style={{ display: 'flex', flex: 1 }} />
                            <ModalButton
                                type={ButtonType.SECONDARY}
                                onClick={onClickDownload}
                                label={stringsRes.common.download}
                            />
                        </div>
                        <div className={classes.lineCurve}>
                            <SvgIcon icon={IconAxel.LineCurve} stroke="unset" size="inherit" height="inherit" />
                        </div>
                    </>
                )}
                {/* Preload wifi icon for offline */}
                <SvgIcon icon={IconCommon.Offline} color="allWhite" size={'none'} />
                <RouterProvider router={router} />
            </Paper>
        </React.Suspense>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootContainerRoot: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            margin: 0,
            height: '100dvh',
            minHeight: '100dvh',
            boxShadow: 'none',
            backgroundColor: theme.palette.background.default,
            '& .gm-style iframe + div': {
                border: 'none!important',
            },
            '& .gmnoprint, .gm-style-cc': {
                display: 'none',
            },
        },
        rootContainerBandeauOffline: {
            color: 'white',
            backgroundColor: 'red',
            height: getTopBarHeight(false) + NavTopBarHeight,
            // top: getTopBarHeight(false),
            position: 'fixed',
            display: 'flex',
            zIndex: 9,
            flex: 1,
            width: '100%',
            alignItems: 'flex-end',
            // alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 20px',
            // padding: '0px 20px',
            textAlign: 'center',
            '& .MuiButtonBase-root': {
                padding: 0,
                minWidth: 'unset',
            },
        },
        rootContainerBandeauDownload: {
            backgroundColor: theme.palette.colors.background.appBar,
            color: theme.palette.primary.main,
            position: 'fixed',
            bottom: 0,
            borderWidth: '1px 0px',
            borderStyle: 'solid',
            borderColor: theme.palette.colors.icon.lightGrey,
            display: 'flex',
            zIndex: 9,
            flex: 1,
            width: '100%',
            textAlign: 'center',
            alignItems: 'center',
            // justifyContent: 'space-between',
            padding: '18px 16px',
            gap: 8,
            '& .MuiButton-root:first-child': {
                display: 'contents',
            },
            '& .MuiButton-contained': {
                padding: '8px 16px',
                minWidth: 'unset',
                fontSize: `${13}px!important`,
            },
        },
        centerBandeauDownload: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            '& .MuiTypography-root': {
                fontSize: `${13}px!important`,
            },
        },
        lineCurve: {
            position: 'fixed',
            width: '100%',
            height: 81,
            bottom: 0,
            zIndex: 9,
            pointerEvents: 'none',
            '@media (min-width: 600px)': {
                display: 'none !important',
            },
        },
    }),
);

const mapStateToProps = (state) => ({
    lang: userSelectors.language(state),
    groupId: currentBookingSelectors.groupId(state),
});

const mapDispatchToProps = {
    changeLanguage: userActions.changeLanguage,
    setGroupId: currentBookingActions.setGroupId,
    setPassengers: currentBookingActions.setPassengers,
    setReservationDate: currentBookingActions.setReservationDate,
    setSelectedService: currentBookingActions.setSelectedService,
    openModal: modalActions.openModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(RootContainer);
